/**
 * Exporting a sheet to PDF, shared by the Sheets screen and the export
 * dialog so both produce the same file: raster views rendered on the live
 * viewer at their print DPI (with a progress dialog and cancel), heatmap
 * legends set to what printed, then the PDF laid out and downloaded. The PDF
 * engine loads when an export starts, not with the page.
 */

import { useCallback, useContext, useRef, useState } from 'react';
import type { PdfImageResource } from '@pointorama/print-engine';
import type { ViewportSettings } from 'shared-types';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { RendererContext } from '../../contexts/RendererContext';
import type { PrintRaster } from '../../potree/utils/PrintRasterRenderer';
import { T } from '../../translation/src';
import { isRasterKind, rasterPdfImage, renderViewportRasters, type RasterRenderProgress } from '../print/raster/viewportRasters';
import type { SheetJobInput } from './sheetLayout';

/** Image keys of rendered raster views in the PDF (and the sheet preview). */
export const RASTER_KEY_PREFIX = 'raster:';

const RasterProgressDialog: React.FC<{ progress: RasterRenderProgress | null; onCancel: () => void }> = ({ progress, onCancel }) => (
  <Modal.Container open={progress !== null} onClose={() => undefined} panelClassName="max-w-sm" className="relative z-[31]">
    <Modal.Header>
      <T _str="rendering views" swc />
    </Modal.Header>
    {progress && (
      <div className="flex flex-col gap-2 text-left text-sm">
        <span>
          <T _str="view {index} of {total}" swc args={{ index: progress.index + 1, total: progress.total }} />
          {' · '}
          {progress.stage === 'loading' ? <T _str="loading the points at full density" /> : <T _str="rendering at print resolution" />}
        </span>
        <div className="h-2 overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
          <div
            className="h-full bg-french-violet-600 transition-[width] dark:bg-neon-green-300"
            style={{ width: `${Math.round(((progress.index + (progress.stage === 'loading' ? 0.5 * progress.fraction : 0.5 + 0.5 * progress.fraction)) / progress.total) * 100)}%` }}
          />
        </div>
      </div>
    )}
    <Modal.Footer className="justify-end">
      <Button variant="secondary" size="xs" onClick={onCancel}>
        <T _str="cancel" swc />
      </Button>
    </Modal.Footer>
  </Modal.Container>
);

export interface SheetPdfExportHooks {
  /** Runs before the viewer is driven for raster views (e.g. stop background previews). */
  beforeRasters?: () => Promise<void>;
  /** Each raster rendered at print resolution (e.g. to refresh the on-screen preview). */
  onRaster?: (key: string, raster: PrintRaster, settings: ViewportSettings) => void;
}

/** Exports a sheet (render rasters → PDF → download); `dialog` must be rendered for the progress / cancel. Throws PrintRasterAbortError when cancelled. */
export function useSheetPdfExport() {
  const { viewer } = useContext(RendererContext);
  const [progress, setProgress] = useState<RasterRenderProgress | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const run = useCallback(
    async (input: SheetJobInput, fileName: string, hooks: SheetPdfExportHooks = {}): Promise<void> => {
      const [{ exportSheetPdf, layoutSheet }, { downloadPdf }] = await Promise.all([import('./sheetLayout'), import('../print/export/exportPdf')]);
      const pages = layoutSheet(input);
      const jobs = pages
        .flatMap((p) => p.viewports)
        .filter((v) => isRasterKind(v.settings.kind))
        .map((v) => ({ key: v.key, box: v.element, settings: v.settings }));
      const pdf = new Map<string, PdfImageResource>();
      let doc = input.doc;
      if (jobs.length > 0 && viewer) {
        await hooks.beforeRasters?.();
        const controller = new AbortController();
        abortRef.current = controller;
        setProgress({ index: 0, total: jobs.length, stage: 'loading', fraction: 0 });
        try {
          const rendered = await renderViewportRasters(viewer, jobs, { signal: controller.signal, onProgress: setProgress });
          const viewports = { ...doc.viewports };
          for (const r of rendered) {
            pdf.set(`${RASTER_KEY_PREFIX}${r.key}`, await rasterPdfImage(r.raster));
            const settings = jobs.find((j) => j.key === r.key)?.settings;
            if (!settings) continue;
            hooks.onRaster?.(r.key, r.raster, settings);
            // The legend shows the range that actually printed.
            if (r.heatmapRange && settings.heatmap) viewports[r.key] = { ...settings, heatmap: { ...settings.heatmap, ...r.heatmapRange } };
          }
          doc = { ...doc, viewports };
        } finally {
          abortRef.current = null;
          setProgress(null);
        }
      }
      const bytes = await exportSheetPdf({
        ...input,
        doc,
        rasterImage: (viewport) => (pdf.has(`${RASTER_KEY_PREFIX}${viewport.key}`) ? `${RASTER_KEY_PREFIX}${viewport.key}` : null),
        rasterPdf: pdf,
      });
      downloadPdf(bytes, fileName);
    },
    [viewer],
  );

  const dialog = <RasterProgressDialog progress={progress} onCancel={() => abortRef.current?.abort()} />;
  return { run, dialog };
}
